import {
	findPageNumbersByClass,
	getFirstPageByClass,
	getPageNumber,
} from './shared/dom-pages.js';
import {
	readHiddenValue,
	readInputValue,
	writeHiddenValue,
} from './shared/input-values.js';
import {
	buildPayloadSignature,
	toInteger,
	toNumber,
} from './shared/payload.js';
import {
	getFormIdFromElement,
	isNextSubmissionType,
	isPreviousSubmissionType,
	isSubmitSubmissionType,
	readCurrentPageNumber,
	readSubmissionSourcePage,
} from './shared/gf-submission.js';
import {
	clearResultMarkupCache,
	readResultMarkupCache,
	writeResultMarkupCache,
} from './shared/result-cache.js';
import {
	invalidateRequest,
	isRequestCurrent,
	nextRequestToken,
	parseRetryAfterMs,
	resolveRetryDelayMs,
	sleepMs,
} from './shared/request-lifecycle.js';
import { dispatchStepStateChange } from './shared/step-state.js';

const REQUIRED_HIDDEN_KEYS = [
	'sd_advice_ctx',
	'sd_advice_hash',
	'sd_roi_status',
];
const ROI_REQUEST_SEQ_DATA_KEY = 'sdConfigRoiRequestSeq';
const ROI_REQUEST_ACTIVE_DATA_KEY = 'sdConfigRoiRequestActive';
const ROI_PAYLOAD_SIGNATURE_DATA_KEY = 'sdConfigRoiPayloadSignature';
const ROI_LAST_ATTEMPT_SIGNATURE_DATA_KEY = 'sdConfigRoiLastAttemptSignature';
const ROI_NO_RESULTS_DATA_KEY = 'sdConfigRoiNoResults';
const ROI_FINALIZE_STATE_DATA_KEY = 'sdConfigRoiFinalizeState';
const ROI_FINALIZE_CTX_DATA_KEY = 'sdConfigRoiFinalizeCtx';
const ROI_FINALIZE_SIGNATURE_DATA_KEY = 'sdConfigRoiFinalizeSignature';
const ROI_FINALIZE_ENTRY_ID_DATA_KEY = 'sdConfigRoiFinalizeEntryId';
const DEFAULT_LOADING_DELAY_MS = 2000;
const DEFAULT_REQUEST_TIMEOUT_MS = 45000;
const DEFAULT_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 2500;
const roiMarkupCacheByForm = new Map();
const roiActiveAbortControllerByForm = new Map();
const registeredPreSubmissionFilterForms = new Set();

/**
* Responsibility:
* - Resolve configured minimum loading delay in milliseconds.
*
* Touches:
* - Reads `config.loadingDelayMs` with fallback to default.
*
* Guarantees:
* - Returns non-negative finite integer delay.
*/
function resolveLoadingDelayMs(config) {
	const parsed = Number.parseInt(String(config?.loadingDelayMs ?? DEFAULT_LOADING_DELAY_MS), 10);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return DEFAULT_LOADING_DELAY_MS;
	}

	return parsed;
}

/**
* Responsibility:
* - Wait for configured minimum loading delay to stabilize UI transitions.
*
* Touches:
* - Uses `window.setTimeout` based on runtime config delay.
*
* Guarantees:
* - Resolves immediately when configured delay is non-positive.
*/
function waitForMinimumLoadingDelay(config) {
	const delayMs = resolveLoadingDelayMs(config);
	if (delayMs <= 0) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		window.setTimeout(resolve, delayMs);
	});
}

/**
* Responsibility:
* - Resolve the hardcoded request timeout for the ROI fetch lifecycle.
*
* Guarantees:
* - Returns the shared hardcoded timeout in milliseconds.
*/
function resolveRequestTimeoutMs() {
	return DEFAULT_REQUEST_TIMEOUT_MS;
}

/**
* Responsibility:
* - Represent ROI request failures with normalized metadata.
*
* Touches:
* - Constructed in `requestResult()` and consumed in result orchestration.
*
* Guarantees:
* - Preserves status/transient/traceId/retryAfterMs/statusCode and failure-diagnostics fields.
*/
class RoiRequestError extends Error {
	constructor(message, options = {}) {
		super(message);
		this.name = 'RoiRequestError';
		this.status = Number(options.status || 0);
		this.transient = options.transient === true;
		this.traceId = String(options.traceId || '');
		this.retryAfterMs = Number(options.retryAfterMs || 0);
		this.statusCode = String(options.statusCode || '');
		this.failureCode = String(options.failureCode || '');
		this.failureStage = String(options.failureStage || '');
		this.whatWentWrong = String(options.whatWentWrong || '');
		this.likelyCause = String(options.likelyCause || '');
		this.howToFix = String(options.howToFix || '');
		this.providerHttpCode = Number(options.providerHttpCode || 0);
		this.providerMessage = String(options.providerMessage || '');
	}
}

/**
* Responsibility:
* - Represent calculator finalize-submission failures with normalized metadata.
*
* Touches:
* - Constructed in `requestFinalizeSubmission()` and consumed in finalize orchestration.
*
* Guarantees:
* - Preserves status/traceId/failureCode/validationMessages metadata for logger output.
*/
class FinalizeSubmissionError extends Error {
	constructor(message, options = {}) {
		super(message);
		this.name = 'FinalizeSubmissionError';
		this.status = Number(options.status || 0);
		this.traceId = String(options.traceId || '');
		this.failureCode = String(options.failureCode || '');
		this.validationMessages = options.validationMessages && typeof options.validationMessages === 'object'
			? options.validationMessages
			: {};
	}
}

/**
* Responsibility:
* - Resolve target GF form element for configured form id.
*
* Touches:
* - Reads `#gform_{formId}` from DOM.
*
* Guarantees:
* - Returns form element or `null` when missing.
*/
function getForm(config) {
	return document.getElementById(`gform_${config.formId}`);
}

/**
* Responsibility:
* - Resolve configured ROI boundary page element.
*
* Touches:
* - Uses class-based page lookup against form DOM.
*
* Guarantees:
* - Returns first matching page or `null`.
*/
function getBoundaryPage(form, config) {
	return getFirstPageByClass(form, config.roiBoundaryClass);
}

/**
* Responsibility:
* - Resolve configured ROI result page element.
*
* Touches:
* - Uses class-based page lookup against form DOM.
*
* Guarantees:
* - Returns first matching page or `null`.
*/
function getResultPage(form, config) {
	return getFirstPageByClass(form, config.roiContainerClass);
}

/**
* Responsibility:
* - Resolve ROI output container from a specific result page.
*
* Touches:
* - Queries configured output class inside page DOM.
*
* Guarantees:
* - Returns container element or `null`.
*/
function getResultContainerFromPage(page, config) {
	if (!(page instanceof HTMLElement)) {
		return null;
	}

	return page.querySelector(`.${config.roiContainerClass}`);
}

/**
* Responsibility:
* - Resolve ROI output container from configured form layout.
*
* Touches:
* - Resolves result page and container class in form DOM.
*
* Guarantees:
* - Returns container element or `null`.
*/
function getResultContainer(form, config) {
	const resultPage = getResultPage(form, config);
	if (!resultPage) {
		return null;
	}

	return getResultContainerFromPage(resultPage, config);
}

/**
* Responsibility:
* - Build ROI request payload from configured input map.
*
* Touches:
* - Reads mapped GF field values by input id and coerces to configured types.
*
* Guarantees:
* - Returns payload object containing every mapped API key.
*/
function buildPayload(form, inputMap) {
	const payload = {};
	const fieldErrors = {};

	for (const [apiKey, map] of Object.entries(inputMap || {})) {
		const ids = Array.isArray(map.inputIds) ? map.inputIds : [];
		const valueType = String(map.valueType || 'string');
		const values = ids
			.map((inputId) => readInputValue(form, inputId))
			.filter((value) => value !== null);
		const candidate = values[0] ?? '';

		if (valueType === 'integer') {
			const parsed = toInteger(candidate);
			payload[apiKey] = parsed;
			if (parsed === null) {
				fieldErrors[apiKey] = `${apiKey} must be an integer value.`;
			}
			continue;
		}

		if (valueType === 'number') {
			const parsed = toNumber(candidate);
			payload[apiKey] = parsed;
			if (parsed === null) {
				fieldErrors[apiKey] = `${apiKey} must be a numeric value.`;
			}
			continue;
		}

		payload[apiKey] = String(candidate || '').trim();
	}

	return {
		payload,
		fieldErrors,
	};
}

/**
* Responsibility:
* - Render provided HTML markup into ROI container.
*
* Touches:
* - Mutates `container.innerHTML`.
*
* Guarantees:
* - Returns `true` only when rendered markup is non-empty.
*/
function setContainerMarkup(container, markup) {
	if (!(container instanceof HTMLElement)) {
		return false;
	}

	const html = typeof markup === 'string' ? markup.trim() : '';
	container.innerHTML = html;
	return html !== '';
}

/**
* Responsibility:
* - Render configured ROI loading markup.
*
* Touches:
* - Delegates to `setContainerMarkup()`.
*
* Guarantees:
* - Applies loading markup when available.
*/
function renderLoading(container, config) {
	setContainerMarkup(container, config.roiLoadingMarkup);
}

/**
* Responsibility:
* - Render configured ROI not-ready markup.
*
* Touches:
* - Delegates to `setContainerMarkup()`.
*
* Guarantees:
* - Applies not-ready markup when available.
*/
function renderNotReady(container, config, message = '') {
	if (!setContainerMarkup(container, config.roiNotReadyMarkup)) {
		return;
	}

	const customMessage = typeof message === 'string' ? message.trim() : '';
	if (customMessage === '') {
		return;
	}

	const messageNode = container.querySelector('.sd-configurator__loader__text');
	if (messageNode) {
		messageNode.textContent = customMessage;
	}
}

/**
* Responsibility:
* - Render configured ROI error markup and wire retry callback.
*
* Touches:
* - Writes error markup, optional message, and binds retry handler.
*
* Guarantees:
* - Retry handler is attached at most once per rendered error state.
*/
function renderError(container, config, message, onRetry) {
	if (!setContainerMarkup(container, config.roiErrorMarkup)) {
		return;
	}

	if (typeof message === 'string' && message.trim() !== '') {
		const messageNode = container.querySelector('.sd-configurator__error-message-marker');
		if (messageNode) {
			messageNode.textContent = message;
		}
	}

	const retryButton = container.querySelector('.sd-config__part2-retry');
	if (retryButton instanceof HTMLButtonElement || retryButton instanceof HTMLInputElement) {
		retryButton.addEventListener('click', () => {
			onRetry();
		}, { once: true });
	}
}

/**
* Responsibility:
* - Write form-scoped ROI loading flag.
*
* Touches:
* - Mutates form dataset loading key.
*
* Guarantees:
* - Stores loading state as `'1'` or `'0'`.
*/
function setRoiLoading(form, loading) {
	form.dataset.sdConfigRoiLoading = loading ? '1' : '0';
}

/**
* Responsibility:
* - Read form-scoped ROI loading state.
*
* Touches:
* - Reads form dataset loading key.
*
* Guarantees:
* - Returns boolean loading flag.
*/
function isRoiLoading(form) {
	return form.dataset.sdConfigRoiLoading === '1';
}

/**
* Responsibility:
* - Write form-scoped ROI no-results state.
*
* Touches:
* - Mutates form dataset no-results key.
*
* Guarantees:
* - Stores no-results state as `'1'` or `'0'`.
*/
function setRoiNoResults(form, noResults) {
	form.dataset[ROI_NO_RESULTS_DATA_KEY] = noResults ? '1' : '0';
}

/**
* Responsibility:
* - Reset form-scoped ROI no-results state.
*
* Touches:
* - Mutates form dataset no-results key.
*
* Guarantees:
* - No-results state becomes `'0'`.
*/
function clearRoiNoResults(form) {
	form.dataset[ROI_NO_RESULTS_DATA_KEY] = '0';
}

/**
* Responsibility:
* - Read form-scoped ROI no-results state.
*
* Touches:
* - Reads form dataset no-results key.
*
* Guarantees:
* - Returns boolean no-results flag.
*/
function isRoiNoResults(form) {
	return form.dataset[ROI_NO_RESULTS_DATA_KEY] === '1';
}

/**
* Responsibility:
* - Read the current finalize-submission lifecycle state for this form.
*
* Touches:
* - Reads the form dataset finalize-state key.
*
* Guarantees:
* - Returns `idle`, `pending`, `success`, or `error`.
*/
function readFinalizeState(form) {
	const state = String(form.dataset[ROI_FINALIZE_STATE_DATA_KEY] || '').trim();
	if (state === 'pending' || state === 'success' || state === 'error') {
		return state;
	}

	return 'idle';
}

/**
* Responsibility:
* - Detect whether a native GF finalize save is currently in flight.
*
* Touches:
* - Reads the dataset state persisted by `writeFinalizeState()`.
*
* Guarantees:
* - Returns true only while finalize state equals `pending`.
*/
function isFinalizePending(form) {
	return readFinalizeState(form) === 'pending';
}

/**
* Responsibility:
* - Persist finalize-submission state for the current ROI signature/context tuple.
*
* Touches:
* - Mutates form dataset keys for finalize state, context token, signature, and entry id.
*
* Guarantees:
* - Stores only normalized values and clears stale metadata when inputs are empty.
*/
function writeFinalizeState(form, state, ctx = '', payloadSignature = '', entryId = 0) {
	const normalizedState = state === 'pending' || state === 'success' || state === 'error'
		? state
		: 'idle';
	form.dataset[ROI_FINALIZE_STATE_DATA_KEY] = normalizedState;
	form.dataset[ROI_FINALIZE_CTX_DATA_KEY] = String(ctx || '').trim();
	form.dataset[ROI_FINALIZE_SIGNATURE_DATA_KEY] = String(payloadSignature || '').trim();
	form.dataset[ROI_FINALIZE_ENTRY_ID_DATA_KEY] = Number.isFinite(Number(entryId)) && Number(entryId) > 0
		? String(Math.trunc(Number(entryId)))
		: '';
}

/**
* Responsibility:
* - Check whether the current ROI signature/context has already been saved to GF.
*
* Touches:
* - Reads finalize state and tuple metadata from the form dataset.
*
* Guarantees:
* - Returns true only for an exact successful finalize match.
*/
function hasSuccessfulFinalizeForSignature(form, ctx, payloadSignature) {
	return readFinalizeState(form) === 'success'
		&& String(form.dataset[ROI_FINALIZE_CTX_DATA_KEY] || '').trim() === String(ctx || '').trim()
		&& String(form.dataset[ROI_FINALIZE_SIGNATURE_DATA_KEY] || '').trim() === String(payloadSignature || '').trim();
}

/**
* Responsibility:
* - Check whether finalize is already pending for the current ROI signature/context.
*
* Touches:
* - Reads finalize state and tuple metadata from the form dataset.
*
* Guarantees:
* - Returns true only for an exact in-flight finalize match.
*/
function hasPendingFinalizeForSignature(form, ctx, payloadSignature) {
	return readFinalizeState(form) === 'pending'
		&& String(form.dataset[ROI_FINALIZE_CTX_DATA_KEY] || '').trim() === String(ctx || '').trim()
		&& String(form.dataset[ROI_FINALIZE_SIGNATURE_DATA_KEY] || '').trim() === String(payloadSignature || '').trim();
}

/**
* Responsibility:
* - Persist successful ROI payload signature for reuse decisions.
*
* Touches:
* - Writes form dataset signature key.
*
* Guarantees:
* - Stores empty string when signature is not usable.
*/
function writeRoiPayloadSignature(form, signature) {
	form.dataset[ROI_PAYLOAD_SIGNATURE_DATA_KEY] = String(signature || '').trim();
}

/**
* Responsibility:
* - Read successful ROI payload signature.
*
* Touches:
* - Reads form dataset signature key.
*
* Guarantees:
* - Returns trimmed signature or empty string.
*/
function readRoiPayloadSignature(form) {
	return String(form.dataset[ROI_PAYLOAD_SIGNATURE_DATA_KEY] || '').trim();
}

/**
* Responsibility:
* - Persist last attempted ROI payload signature.
*
* Touches:
* - Writes form dataset attempt-signature key.
*
* Guarantees:
* - Stores empty string when signature is not usable.
*/
function writeRoiAttemptSignature(form, signature) {
	form.dataset[ROI_LAST_ATTEMPT_SIGNATURE_DATA_KEY] = String(signature || '').trim();
}

/**
* Responsibility:
* - Read last attempted ROI payload signature.
*
* Touches:
* - Reads form dataset attempt-signature key.
*
* Guarantees:
* - Returns trimmed signature or empty string.
*/
function readRoiAttemptSignature(form) {
	return String(form.dataset[ROI_LAST_ATTEMPT_SIGNATURE_DATA_KEY] || '').trim();
}

/**
* Responsibility:
* - Persist active ROI AbortController for current form.
*
* Touches:
* - Writes module-level abort controller map.
*
* Guarantees:
* - Replaces any previous controller for the same form id.
*/
function writeActiveRoiAbortController(formId, controller) {
	if (!(controller instanceof AbortController)) {
		return;
	}

	roiActiveAbortControllerByForm.set(String(formId), controller);
}

/**
* Responsibility:
* - Clear active ROI AbortController reference for current form.
*
* Touches:
* - Mutates module-level abort controller map.
*
* Guarantees:
* - Clears only expected controller when provided.
*/
function clearActiveRoiAbortController(formId, expectedController = null) {
	const key = String(formId);
	const currentController = roiActiveAbortControllerByForm.get(key);
	if (!(currentController instanceof AbortController)) {
		roiActiveAbortControllerByForm.delete(key);
		return;
	}

	if (expectedController instanceof AbortController && currentController !== expectedController) {
		return;
	}

	roiActiveAbortControllerByForm.delete(key);
}

/**
* Responsibility:
* - Abort in-flight ROI request for the provided form id.
*
* Touches:
* - Calls `AbortController.abort()` and clears controller map entry.
*
* Guarantees:
* - No-op when no active controller exists.
*/
function abortActiveRoiRequest(formId) {
	const key = String(formId);
	const controller = roiActiveAbortControllerByForm.get(key);
	if (!(controller instanceof AbortController)) {
		roiActiveAbortControllerByForm.delete(key);
		return;
	}

	controller.abort();
	roiActiveAbortControllerByForm.delete(key);
}

/**
* Responsibility:
* - Resolve result-page next button for ROI step UI sync.
*
* Touches:
* - Queries `.gform_next_button` inside configured result page.
*
* Guarantees:
* - Returns button/input element or `null`.
*/
function getResultNextButton(form, config) {
	const page = getResultPage(form, config);
	if (!page) {
		return null;
	}

	const button = page.querySelector('.gform_next_button');
	if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
		return button;
	}

	return null;
}

/**
* Responsibility:
* - Resolve result-page submit button for ROI step UI sync.
*
* Touches:
* - Queries `submit` control inside configured result page.
*
* Guarantees:
* - Returns button/input element or `null`.
*/
function getResultSubmitButton(form, config) {
	const page = getResultPage(form, config);
	if (!page) {
		return null;
	}

	const button = page.querySelector('button[type="submit"], input[type="submit"]');
	if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
		return button;
	}

	return null;
}

/**
* Responsibility:
* - Check whether ROI state is ready for required gating.
*
* Touches:
* - Reads mapped `sd_roi_status` hidden field value.
*
* Guarantees:
* - Returns true only when status equals configured success code.
*/
function isRoiReady(form, config) {
	const status = readHiddenValue(form, config.hiddenFieldMap, 'sd_roi_status');
	return status === config.statuses.success;
}

/**
* Responsibility:
* - Compute whether result page actions should be disabled.
*
* Touches:
* - Reads loading/finalize-pending form state and current ROI tuple finalize state.
*
* Guarantees:
* - Blocks actions while ROI request loading, native finalize save is pending, or the current ROI result is already stored.
*/
function shouldDisableResultAction(form, config) {
	return isRoiLoading(form)
		|| isFinalizePending(form)
		|| hasSuccessfulFinalizeForCurrentState(form, config);
}

/**
* Responsibility:
* - Detect whether the current advice-context and ROI payload tuple was already stored.
*
* Touches:
* - Reads hidden advice context, rebuilds the current ROI payload, and compares against finalize dataset state.
*
* Guarantees:
* - Returns true only when the current ROI result matches a previously successful native finalize save.
*/
function hasSuccessfulFinalizeForCurrentState(form, config) {
	const ctx = readHiddenValue(form, config.hiddenFieldMap, 'sd_advice_ctx');
	if (ctx === '') {
		return false;
	}

	const { payload } = buildPayload(form, config.roiInputMap);
	const payloadSignature = buildPayloadSignature(payload);
	if (payloadSignature === '') {
		return false;
	}

	return hasSuccessfulFinalizeForSignature(form, ctx, payloadSignature);
}

/**
* Responsibility:
* - Synchronize result page next/submit disabled state with ROI truth state.
*
* Touches:
* - Mutates next and submit button `disabled` + data marker attributes.
*
* Guarantees:
* - Both buttons reflect same blocked/unblocked state.
*/
function syncResultState(form, config) {
	const shouldBlock = shouldDisableResultAction(form, config);

	const nextButton = getResultNextButton(form, config);
	if (nextButton instanceof HTMLButtonElement || nextButton instanceof HTMLInputElement) {
		nextButton.disabled = shouldBlock;
		nextButton.dataset.sdConfigRoiBlocked = shouldBlock ? '1' : '0';
	}

	const submitButton = getResultSubmitButton(form, config);
	if (submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement) {
		submitButton.disabled = shouldBlock;
		submitButton.dataset.sdConfigRoiBlocked = shouldBlock ? '1' : '0';
	}

	dispatchStepStateChange(form, 'roi_state_sync');
}

/**
* Responsibility:
* - Detect whether ROI result container currently has rendered markup.
*
* Touches:
* - Reads `container.innerHTML`.
*
* Guarantees:
* - Returns true only when container exists and markup is non-empty.
*/
function hasRenderedResult(container) {
	if (!(container instanceof HTMLElement)) {
		return false;
	}

	return String(container.innerHTML || '').trim() !== '';
}

/**
* Responsibility:
* - Read deterministic no-results marker from ROI result markup.
*
* Touches:
* - Reads the rendered ROI result no-results CSS class marker.
*
* Guarantees:
* - Returns `true`/`false` when marker is detectable, otherwise `null`.
*/
function readNoResultsFromContainer(container) {
	if (!(container instanceof HTMLElement)) {
		return null;
	}

	const resultNode = container.querySelector('.sd-configurator__result');
	if (!(resultNode instanceof HTMLElement)) {
		return null;
	}

	return resultNode.classList.contains('sd-configurator__result__no-results');
}

/**
* Responsibility:
* - Synchronize ROI no-results state from rendered markup marker.
*
* Touches:
* - Reads marker via `readNoResultsFromContainer()` and writes dataset state.
*
* Guarantees:
* - Returns true only when marker was found and synchronized.
*/
function syncNoResultsFromContainer(form, container) {
	const detected = readNoResultsFromContainer(container);
	if (typeof detected !== 'boolean') {
		return false;
	}

	setRoiNoResults(form, detected);
	return true;
}

/**
* Responsibility:
* - Restore ROI markup/no-results state from per-form signature cache.
*
* Touches:
* - Reads shared cache map and writes rendered markup + no-results state.
*
* Guarantees:
* - Returns true only when cache rehydration succeeds.
*/
function rehydrateRoiFromCache(app, form, container, signature) {
	const cached = readResultMarkupCache(roiMarkupCacheByForm, app.config.formId, signature);
	if (!cached) {
		return false;
	}

	if (!setContainerMarkup(container, cached.markup)) {
		return false;
	}

	setRoiNoResults(form, cached.noResults);
	syncResultState(form, app.config);
	return true;
}

/**
* Responsibility:
* - Build tracked input names set for ROI invalidation listeners.
*
* Touches:
* - Derives candidate field names from `roiInputMap` ids.
*
* Guarantees:
* - Returns set with base and array-style GF input names.
*/
function buildTrackedNames(inputMap) {
	const names = new Set();

	Object.values(inputMap || {}).forEach((map) => {
		const ids = Array.isArray(map.inputIds) ? map.inputIds : [];
		ids.forEach((inputId) => {
			const baseNames = [
				`input_${inputId}`,
				`input_${String(inputId).replace('.', '_')}`,
			];
			baseNames.forEach((baseName) => {
				names.add(baseName);
				names.add(`${baseName}[]`);
			});
		});
	});

	return names;
}

/**
* Responsibility:
* - Validate required hidden-field map keys for ROI runtime state.
*
* Touches:
* - Reads `config.hiddenFieldMap` values for required keys.
*
* Guarantees:
* - Returns list of missing keys.
*/
function validateHiddenFieldMap(config) {
	return REQUIRED_HIDDEN_KEYS.filter((key) => {
		const fieldId = config.hiddenFieldMap?.[key];
		return typeof fieldId !== 'string' || fieldId.trim() === '';
	});
}

/**
* Responsibility:
* - Validate configured ROI boundary/result page layout assumptions.
*
* Touches:
* - Reads boundary/result class matches and relative page order.
*
* Guarantees:
* - Returns true only when layout is valid for dedicated ROI step flow.
*/
function validateLayout(app, form) {
	const boundaryPages = findPageNumbersByClass(form, app.config.roiBoundaryClass);
	const resultPages = findPageNumbersByClass(form, app.config.roiContainerClass);

	if (boundaryPages.length !== 1 || resultPages.length !== 1) {
		app.logger.error('ROI result layout invalid: expected exactly one boundary page and one result page.', {
			formId: app.config.formId,
			boundaryClass: app.config.roiBoundaryClass,
			containerClass: app.config.roiContainerClass,
			boundaryPages,
			resultPages,
			whatWentWrong: 'Boundary and/or result selectors do not resolve to exactly one page.',
			likelyCause: 'Boundary/result class is missing, duplicated, or applied on the wrong page.',
			howToFix: `Use "${app.config.roiBoundaryClass}" on one boundary page and "${app.config.roiContainerClass}" on one dedicated result page.`,
		});
		return false;
	}

	if (resultPages[0] <= boundaryPages[0]) {
		app.logger.error('ROI result layout invalid: result page must come after boundary page.', {
			formId: app.config.formId,
			boundaryPage: boundaryPages[0],
			resultPage: resultPages[0],
			boundaryClass: app.config.roiBoundaryClass,
			containerClass: app.config.roiContainerClass,
			whatWentWrong: 'Configured ROI result page is on or before the configured boundary page.',
			likelyCause: 'Boundary class is placed on the result page, or result class is placed too early in the flow.',
			howToFix: `Move "${app.config.roiBoundaryClass}" to the page where Next triggers ROI and keep "${app.config.roiContainerClass}" on a later page.`,
		});
		return false;
	}

	return true;
}

/**
* Responsibility:
* - Decide whether request errors should be treated as transient/retriable.
*
* Touches:
* - Reads normalized error metadata (`transient`, `status`).
*
* Guarantees:
* - Returns true for network, 429, and 5xx-like failures.
*/
function isTransientError(error) {
	if (error?.transient === true) {
		return true;
	}

	const status = Number(error?.status || 0);
	if (status === 0 || status === 429) {
		return true;
	}

	return status >= 500;
}

/**
* Responsibility:
* - Resolve precise diagnostics for ROI request failures.
*
* Touches:
* - Consumes `RoiRequestError` or REST result metadata when available and falls back to status-based inference otherwise.
*
* Guarantees:
* - Returns stable failure details for console logging without discarding server-provided context.
*/
function resolveRoiRequestFailureDetails(error) {
	const whatWentWrong = String(error?.whatWentWrong || '').trim();
	const likelyCause = String(error?.likelyCause || '').trim();
	const howToFix = String(error?.howToFix || '').trim();
	if (whatWentWrong !== '' || likelyCause !== '' || howToFix !== '') {
		return {
			failureCode: String(error?.failureCode || '').trim(),
			failureStage: String(error?.failureStage || '').trim(),
			providerHttpCode: Number(error?.providerHttpCode || 0),
			providerMessage: String(error?.providerMessage || '').trim(),
			whatWentWrong,
			likelyCause,
			howToFix,
		};
	}

	const status = Number(error?.status || 0);

	return {
		failureCode: String(error?.failureCode || '').trim(),
		failureStage: String(error?.failureStage || '').trim(),
		providerHttpCode: Number(error?.providerHttpCode || 0),
		providerMessage: String(error?.providerMessage || '').trim(),
		whatWentWrong: status === 422
			? 'ROI result endpoint rejected the request or one or more machine calculations failed.'
			: 'ROI result request did not complete with a successful payload.',
		likelyCause: status === 401 || status === 403
			? 'Security validation failed (missing/expired nonce or blocked origin).'
			: status === 404
				? 'ROI result endpoint was not found.'
				: status === 409
					? 'Stored machine advice state is stale, expired, or mismatched for this ROI request.'
					: status === 422
						? 'The request reached the backend, but either the request payload or one or more upstream machine calculations were invalid.'
						: status === 429
							? 'Result route or upstream API is rate-limiting requests.'
							: status >= 500
								? 'Server-side result handling or upstream API failed while calculating ROI.'
								: 'Request failed before a successful ROI result response was returned.',
		howToFix: status === 401 || status === 403
			? 'Reload the page for a fresh nonce and verify REST nonce/header checks and allowed origin.'
			: status === 404
				? 'Verify the result REST route is registered and `roiResultEndpoint` points to the correct URL.'
				: status === 409
					? 'Fetch machine advice again so ROI uses a fresh advice context and hash.'
					: status === 422
						? 'Inspect the REST response body, row diagnostics, and traceId-backed plugin logs to see which machine request failed.'
						: status === 429
							? 'Retry after cooldown and check API/provider rate-limit settings.'
							: status >= 500
								? 'Review server logs and upstream API logs using the traceId to identify backend failures.'
								: 'Check browser Network tab, CORS, and site connectivity.',
	};
}

/**
* Responsibility:
* - Extract per-machine ROI failure details from result rows for logging.
*
* Touches:
* - Reads `rows` returned by the ROI REST response.
*
* Guarantees:
* - Returns one normalized object per failed machine row and skips successful rows.
*/
function collectRoiRowFailures(rows) {
	if (!Array.isArray(rows)) {
		return [];
	}

	return rows
		.filter((row) => row && typeof row === 'object' && String(row.state || '') === 'error')
		.map((row) => ({
			machineId: String(row.machineId || '').trim(),
			machineLabel: String(row.machineLabel || '').trim(),
			errorMessage: String(row.errorMessage || '').trim(),
			failureCode: String(row.failureCode || '').trim(),
			failureStage: String(row.failureStage || '').trim(),
			providerHttpCode: Number(row.providerHttpCode || 0),
			providerMessage: String(row.providerMessage || '').trim(),
			whatWentWrong: String(row.whatWentWrong || '').trim(),
			likelyCause: String(row.likelyCause || '').trim(),
			howToFix: String(row.howToFix || '').trim(),
			transient: row.transient === true,
		}));
}

/**
* Responsibility:
* - Collect all current GF `input_*` values for native finalize submission.
*
* Touches:
* - Reads current form control values via `FormData`.
*
* Guarantees:
* - Returns an object containing only `input_*` values with repeated keys grouped into arrays.
*/
function collectSubmissionInputValues(form) {
	const submission = {};
	const formData = new FormData(form);

	for (const [key, value] of formData.entries()) {
		if (typeof key !== 'string' || !key.startsWith('input_') || value instanceof File) {
			continue;
		}

		const normalizedValue = String(value);
		if (!Object.prototype.hasOwnProperty.call(submission, key)) {
			submission[key] = normalizedValue;
			continue;
		}

		if (Array.isArray(submission[key])) {
			submission[key].push(normalizedValue);
			continue;
		}

		submission[key] = [
			String(submission[key]),
			normalizedValue,
		];
	}

	return submission;
}

/**
* Responsibility:
* - Request a native Gravity Forms finalize save for the successful ROI result.
*
* Touches:
* - Performs `fetch` POST to the finalize endpoint with current form values and ROI payload.
*
* Guarantees:
* - Returns the parsed successful response body.
* - Throws `FinalizeSubmissionError` for transport, protocol, or validation failures.
*/
async function requestFinalizeSubmission(config, form, ctx, adviceHash, payload, page, logger) {
	const endpoint = String(config?.finalizeSubmissionEndpoint || '').trim();
	const nonce = String(config?.finalizeSubmissionNonce || '').trim();
	if (endpoint === '' || nonce === '') {
		throw new FinalizeSubmissionError('Calculator finalize submission is not configured.', {
			status: 0,
			failureCode: 'finalize_config_missing',
		});
	}

	let response;
	try {
		response = await fetch(endpoint, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'X-SD-Config-Nonce': nonce,
				'X-WP-Nonce': nonce,
			},
			body: JSON.stringify({
				formId: config.formId,
				_wpnonce: nonce,
				ctx,
				adviceHash,
				sourcePage: getPageNumber(page),
				roiPayload: payload,
				submission: collectSubmissionInputValues(form),
			}),
		});
	} catch (_error) {
		throw new FinalizeSubmissionError('Calculator finalize submission request failed before reaching the server.', {
			status: 0,
			failureCode: 'finalize_transport_error',
		});
	}

	let body = {};
	try {
		body = await response.json();
	} catch (_error) {
		logger.error('ROI finalize submission failed: endpoint returned a non-JSON response.', {
			status: response.status,
			endpoint,
			whatWentWrong: 'Finalize submission response body could not be parsed as JSON.',
			likelyCause: 'Endpoint returned HTML/error output instead of a JSON payload.',
			howToFix: 'Inspect the finalize submission response in the browser Network tab and verify the route returns JSON.',
		});

		throw new FinalizeSubmissionError('Calculator finalize submission returned an invalid response.', {
			status: response.status,
			failureCode: 'finalize_response_not_json',
		});
	}

	if (!response.ok || body?.success !== true) {
		throw new FinalizeSubmissionError(body?.message || 'Calculator finalize submission failed.', {
			status: response.status,
			traceId: body?.traceId || '',
			failureCode: body?.failureCode || '',
			validationMessages: body?.validationMessages || {},
		});
	}

	return body;
}

/**
* Responsibility:
* - Finalize a successful ROI result as a native Gravity Forms entry exactly once per signature.
*
* Touches:
* - Writes finalize state to the form dataset, performs the finalize request, and syncs result controls.
*
* Guarantees:
* - Prevents duplicate finalize calls for the same ROI signature/context tuple.
* - Leaves result controls enabled again after success or failure.
*/
async function finalizeSuccessfulResult({
	app,
	form,
	page,
	ctx,
	adviceHash,
	payload,
	payloadSignature,
}) {
	if (
		ctx === ''
		|| adviceHash === ''
		|| payloadSignature === ''
		|| isRoiNoResults(form)
		|| hasSuccessfulFinalizeForSignature(form, ctx, payloadSignature)
		|| hasPendingFinalizeForSignature(form, ctx, payloadSignature)
	) {
		return;
	}

	writeFinalizeState(form, 'pending', ctx, payloadSignature, 0);
	syncResultState(form, app.config);

	try {
		const result = await requestFinalizeSubmission(app.config, form, ctx, adviceHash, payload, page, app.logger);
		const entryId = Number(result?.entryId || 0);
		writeFinalizeState(form, 'success', ctx, payloadSignature, entryId);
		app.logger.debug('ROI result finalized as a native Gravity Forms entry.', {
			formId: app.config.formId,
			ctx,
			payloadSignature,
			entryId,
			alreadyFinalized: result?.alreadyFinalized === true,
			traceId: result?.traceId || '',
		});
	} catch (error) {
		const validationMessages = error?.validationMessages && typeof error.validationMessages === 'object'
			? error.validationMessages
			: {};
		writeFinalizeState(form, 'error', ctx, payloadSignature, 0);
		app.logger.error('ROI result finalize submission failed.', {
			formId: app.config.formId,
			ctx,
			payloadSignature,
			status: error?.status || 0,
			message: error?.message || '',
			traceId: error?.traceId || '',
			failureCode: error?.failureCode || '',
			validationMessages,
			whatWentWrong: 'The calculator result could not be stored as a native Gravity Forms entry.',
			likelyCause: 'Finalize submission validation failed or the finalize endpoint could not persist the entry.',
			howToFix: 'Inspect the finalize endpoint response and matching plugin logs using the trace id, then recalculate the ROI if needed.',
		});
	} finally {
		syncResultState(form, app.config);
	}
}

/**
* Responsibility:
* - Opportunistically finalize an already-successful ROI result during cache reuse or rehydration.
*
* Touches:
* - Reads current hidden advice state and dispatches finalize work without blocking render reuse.
*
* Guarantees:
* - Triggers finalize only when the ROI result is successful, non-empty, and not already finalized for the same signature.
*/
function maybeFinalizeSuccessfulResult({
	app,
	form,
	page,
	payload,
	payloadSignature,
}) {
	if (!isRoiReady(form, app.config) || isRoiNoResults(form)) {
		return;
	}

	const ctx = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_advice_ctx');
	const adviceHash = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_advice_hash');
	if (ctx === '' || adviceHash === '' || payloadSignature === '') {
		return;
	}

	if (
		hasSuccessfulFinalizeForSignature(form, ctx, payloadSignature)
		|| hasPendingFinalizeForSignature(form, ctx, payloadSignature)
	) {
		return;
	}

	void finalizeSuccessfulResult({
		app,
		form,
		page,
		ctx,
		adviceHash,
		payload,
		payloadSignature,
	});
}

/**
* Responsibility:
* - Request ROI result from server endpoint with nonce/auth headers.
*
* Touches:
* - Performs `fetch` POST to ROI result endpoint with ctx/hash/payload body.
* - Applies timeout-driven abort when controller is provided.
*
* Guarantees:
* - Returns parsed response body on handled success/stale/error statuses.
* - Throws `RoiRequestError` for transport/shape/protocol failures.
*/
async function requestResult(config, ctx, adviceHash, payload, logger, controller = null) {
	const timeoutMs = resolveRequestTimeoutMs();
	let timeoutId = 0;
	if (controller && timeoutMs > 0) {
		timeoutId = window.setTimeout(() => {
			controller.abort();
		}, timeoutMs);
	}

	let response;
	try {
		response = await fetch(config.roiResultEndpoint, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'X-SD-Config-Nonce': config.roiResultNonce,
				'X-WP-Nonce': config.roiResultNonce,
			},
			body: JSON.stringify({
				formId: config.formId,
				_wpnonce: config.roiResultNonce,
				ctx,
				adviceHash,
				payload,
			}),
			signal: controller ? controller.signal : undefined,
		});
	} catch (error) {
		if (timeoutId > 0) {
			window.clearTimeout(timeoutId);
		}

		const aborted = controller ? controller.signal.aborted === true : error?.name === 'AbortError';
		throw new RoiRequestError(config.roiErrorText || '', {
			status: 0,
			transient: true,
			traceId: '',
			retryAfterMs: aborted ? timeoutMs : 0,
			failureCode: aborted ? 'request_aborted' : 'request_transport_error',
			failureStage: aborted ? 'browser_request_timeout' : 'browser_request_transport',
			whatWentWrong: aborted
				? 'ROI result request was aborted before a response was received.'
				: 'Browser could not complete the request to the ROI result endpoint.',
			likelyCause: aborted
				? 'The request timed out or a newer ROI request replaced the current one.'
				: 'Network, DNS, CORS, or site connectivity prevented the request from completing.',
			howToFix: aborted
				? 'Retry the calculation and check whether repeated page/input transitions are cancelling the request.'
				: 'Check the browser Network tab and site connectivity, then retry the calculation.',
			providerHttpCode: 0,
			providerMessage: '',
		});
	}

	if (timeoutId > 0) {
		window.clearTimeout(timeoutId);
	}

	let body = {};
	try {
		body = await response.json();
	} catch (_error) {
		logger.error('ROI result failed: endpoint returned a non-JSON response.', {
			status: response.status,
			endpoint: config.roiResultEndpoint,
			whatWentWrong: 'Response body could not be parsed as JSON.',
			likelyCause: 'Endpoint returned HTML/error output (for example a PHP warning, proxy page, or auth redirect).',
			howToFix: 'Inspect the result request response body in browser Network tab and make sure the route returns valid JSON.',
		});

		throw new RoiRequestError(config.roiErrorText || '', {
			status: response.status,
			transient: response.status === 0 || response.status === 429 || response.status >= 500,
			traceId: response.headers.get('X-SD-Trace-Id') || '',
			retryAfterMs: parseRetryAfterMs(response),
			failureCode: 'rest_response_not_json',
			failureStage: 'rest_response_decode',
			whatWentWrong: 'Response body could not be parsed as JSON.',
			likelyCause: 'Endpoint returned HTML/error output (for example a PHP warning, proxy page, or auth redirect).',
			howToFix: 'Inspect the result request response body in browser Network tab and make sure the route returns valid JSON.',
			providerHttpCode: 0,
			providerMessage: '',
		});
	}

	const statusCode = String(body?.status || '').trim();
	if (statusCode === config.statuses.success || statusCode === config.statuses.error || statusCode === config.statuses.stale) {
		return {
			...body,
			httpStatus: response.status,
			transient: body?.transient === true,
			traceId: body?.traceId || response.headers.get('X-SD-Trace-Id') || '',
		};
	}

	if (!response.ok || body.success !== true) {
		throw new RoiRequestError(body?.message || config.roiErrorText || '', {
			status: response.status,
			statusCode,
			transient: body?.transient === true || response.status === 429 || response.status >= 500,
			traceId: body?.traceId || response.headers.get('X-SD-Trace-Id') || '',
			retryAfterMs: parseRetryAfterMs(response),
			failureCode: body?.failureCode || '',
			failureStage: body?.failureStage || '',
			whatWentWrong: body?.whatWentWrong || '',
			likelyCause: body?.likelyCause || '',
			howToFix: body?.howToFix || '',
			providerHttpCode: body?.providerHttpCode || 0,
			providerMessage: body?.providerMessage || '',
		});
	}

	return body;
}

/**
* Responsibility:
* - Compute deterministic no-results flag from response payload and rendered markup.
*
* Touches:
* - Reads `result.rows` and rendered marker fallback.
*
* Guarantees:
* - Returns boolean no-results state.
*/
function deriveNoResults(result, container) {
	if (Array.isArray(result?.rows)) {
		return result.rows.length === 0;
	}

	const fromMarker = readNoResultsFromContainer(container);
	return fromMarker === true;
}

/**
* Responsibility:
* - Execute ROI result request/render lifecycle on dedicated result page.
*
* Touches:
* - Mutates loading/status/signature/no-results states and result container markup.
* - Uses request token + abort controller to guard stale responses.
*
* Guarantees:
* - Maintains consistent state transitions for success/error/stale outcomes.
*/
async function resultAtRoiStep({
	app,
	form,
	page,
	autoRetry = true,
	payloadOverride = null,
	payloadFieldErrorsOverride = null,
	payloadSignatureOverride = '',
}) {
	if (isRoiLoading(form)) {
		return;
	}

	const container = getResultContainerFromPage(page, app.config);
	if (!container) {
		return;
	}

	const ctx = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_advice_ctx');
	const adviceHash = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_advice_hash');
	if (ctx === '' || adviceHash === '') {
		clearRoiNoResults(form);
		writeRoiPayloadSignature(form, '');
		writeRoiAttemptSignature(form, '');
		writeFinalizeState(form, 'idle', '', '', 0);
		writeHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status', app.config.statuses.stale);
		renderNotReady(container, app.config);
		syncResultState(form, app.config);
		return;
	}

	const payloadResult = payloadOverride && typeof payloadOverride === 'object'
		? {
			payload: payloadOverride,
			fieldErrors: payloadFieldErrorsOverride && typeof payloadFieldErrorsOverride === 'object'
				? payloadFieldErrorsOverride
				: {},
		}
		: buildPayload(form, app.config.roiInputMap);
	const payload = payloadResult.payload;
	const payloadFieldErrors = payloadResult.fieldErrors || {};
	const payloadSignature = String(payloadSignatureOverride || '').trim() || buildPayloadSignature(payload);
	writeRoiAttemptSignature(form, payloadSignature);

	if (Object.keys(payloadFieldErrors).length > 0) {
		clearRoiNoResults(form);
		writeRoiPayloadSignature(form, '');
		writeFinalizeState(form, 'idle', '', '', 0);
		writeHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status', app.config.statuses.error);
		syncResultState(form, app.config);
		renderError(
			container,
			app.config,
			String(Object.values(payloadFieldErrors)[0] || 'One or more ROI inputs are invalid.').trim(),
			() => {
				resultAtRoiStep({
					app,
					form,
					page,
					autoRetry: false,
				});
			}
		);
		return;
	}

	const formId = Number(app.config.formId || 0);
	abortActiveRoiRequest(formId);
	const controller = typeof AbortController === 'function' ? new AbortController() : null;
	if (controller) {
		writeActiveRoiAbortController(formId, controller);
	}

	setRoiLoading(form, true);
	syncResultState(form, app.config);
	renderLoading(container, app.config);
	const requestToken = nextRequestToken(form, ROI_REQUEST_SEQ_DATA_KEY, ROI_REQUEST_ACTIVE_DATA_KEY);
	const minimumLoadingDelay = waitForMinimumLoadingDelay(app.config);

	try {
		const result = await requestResult(app.config, ctx, adviceHash, payload, app.logger, controller);
		await minimumLoadingDelay;
		if (!isRequestCurrent(form, ROI_REQUEST_ACTIVE_DATA_KEY, requestToken)) {
			clearActiveRoiAbortController(formId, controller);
			return;
		}

		const status = String(result?.status || app.config.statuses.error);
		writeHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status', status);

		if (status === app.config.statuses.stale) {
			clearRoiNoResults(form);
			writeRoiPayloadSignature(form, '');
			writeFinalizeState(form, 'idle', '', '', 0);
			renderNotReady(container, app.config, result?.message);
			return;
		}

		if (typeof result?.markup === 'string' && result.markup.trim() !== '') {
			setContainerMarkup(container, result.markup);
			const noResults = deriveNoResults(result, container);
			setRoiNoResults(form, noResults);
			writeResultMarkupCache(roiMarkupCacheByForm, app.config.formId, payloadSignature, container.innerHTML, noResults);

			if (status === app.config.statuses.success) {
				writeRoiPayloadSignature(form, payloadSignature);
				await finalizeSuccessfulResult({
					app,
					form,
					page,
					ctx,
					adviceHash,
					payload,
					payloadSignature,
				});
			} else {
				writeRoiPayloadSignature(form, '');
				writeFinalizeState(form, 'idle', '', '', 0);

				const diagnostics = resolveRoiRequestFailureDetails(result);
				app.logger.error('ROI result request completed with one or more machine failures.', {
					status,
					message: result?.message,
					transient: result?.transient === true,
					traceId: result?.traceId || '',
					failureCode: diagnostics.failureCode,
					failureStage: diagnostics.failureStage,
					providerHttpCode: diagnostics.providerHttpCode,
					providerMessage: diagnostics.providerMessage,
					whatWentWrong: diagnostics.whatWentWrong,
					likelyCause: diagnostics.likelyCause,
					howToFix: diagnostics.howToFix,
					rowFailures: collectRoiRowFailures(result?.rows),
				});
			}
			return;
		}

		if (autoRetry && isTransientError(result)) {
			setRoiLoading(form, false);
			const retryDelayMs = resolveRetryDelayMs(result, 1, {
				baseDelayMs: DEFAULT_RETRY_DELAY_MS,
				maxDelayMs: MAX_RETRY_DELAY_MS,
			});
			await sleepMs(retryDelayMs);
			await resultAtRoiStep({
				app,
				form,
				page,
				autoRetry: false,
				payloadOverride: payload,
				payloadSignatureOverride: payloadSignature,
			});
			return;
		}

		clearRoiNoResults(form);
		writeRoiPayloadSignature(form, '');
		writeFinalizeState(form, 'idle', '', '', 0);
		renderError(container, app.config, result?.message, () => {
			resultAtRoiStep({
				app,
				form,
				page,
				autoRetry: false,
			});
		});
	} catch (error) {
		await minimumLoadingDelay;
		if (!isRequestCurrent(form, ROI_REQUEST_ACTIVE_DATA_KEY, requestToken)) {
			clearActiveRoiAbortController(formId, controller);
			return;
		}

		const staleState = String(error?.statusCode || '') === app.config.statuses.stale;
		if (staleState) {
			clearRoiNoResults(form);
			writeRoiPayloadSignature(form, '');
			writeFinalizeState(form, 'idle', '', '', 0);
			writeHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status', app.config.statuses.stale);
			renderNotReady(container, app.config, error?.message);
		} else {
			writeHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status', app.config.statuses.error);
			if (autoRetry && isTransientError(error)) {
				setRoiLoading(form, false);
				const retryDelayMs = resolveRetryDelayMs(error, 1, {
					baseDelayMs: DEFAULT_RETRY_DELAY_MS,
					maxDelayMs: MAX_RETRY_DELAY_MS,
				});
				await sleepMs(retryDelayMs);
				await resultAtRoiStep({
					app,
					form,
					page,
					autoRetry: false,
					payloadOverride: payload,
					payloadSignatureOverride: payloadSignature,
				});
				return;
			}

			const diagnostics = resolveRoiRequestFailureDetails(error);
			app.logger.error('ROI result request failed.', {
				status: error?.status,
				message: error?.message,
				transient: error?.transient,
				traceId: error?.traceId || '',
				failureCode: diagnostics.failureCode,
				failureStage: diagnostics.failureStage,
				providerHttpCode: diagnostics.providerHttpCode,
				providerMessage: diagnostics.providerMessage,
				whatWentWrong: diagnostics.whatWentWrong,
				likelyCause: diagnostics.likelyCause,
				howToFix: diagnostics.howToFix,
			});

			clearRoiNoResults(form);
			writeRoiPayloadSignature(form, '');
			writeFinalizeState(form, 'idle', '', '', 0);
			renderError(container, app.config, error?.message, () => {
				resultAtRoiStep({
					app,
					form,
					page,
					autoRetry: false,
				});
			});
		}
	} finally {
		clearActiveRoiAbortController(formId, controller);
		if (!isRequestCurrent(form, ROI_REQUEST_ACTIVE_DATA_KEY, requestToken)) {
			return;
		}

		form.dataset[ROI_REQUEST_ACTIVE_DATA_KEY] = '';
		setRoiLoading(form, false);
		syncResultState(form, app.config);
	}
}

/**
* Responsibility:
* - Compute deterministic fetch action for current ROI payload/state tuple.
*
* Touches:
* - Uses only provided state inputs (pure helper).
*
* Guarantees:
* - Returns `{ action, reason }` where action is `skip|rehydrate|fetch`.
*/
function getRoiFetchDecision({
	force,
	roiReady,
	status,
	errorStatus,
	currentPayloadSignature,
	successfulPayloadSignature,
	attemptedPayloadSignature,
	hasRenderedMarkup,
	hasCachedMarkup,
}) {
	if (force) {
		return {
			action: 'fetch',
			reason: 'forced',
		};
	}

	const sameSuccessfulSignature = roiReady
		&& successfulPayloadSignature !== ''
		&& currentPayloadSignature !== ''
		&& successfulPayloadSignature === currentPayloadSignature;
	if (sameSuccessfulSignature) {
		if (hasRenderedMarkup) {
			return {
				action: 'skip',
				reason: 'reuse_rendered',
			};
		}

		if (hasCachedMarkup) {
			return {
				action: 'rehydrate',
				reason: 'reuse_cached_markup',
			};
		}

		return {
			action: 'fetch',
			reason: 'success_without_rendered_markup',
		};
	}

	const sameFailedSignature = status === errorStatus
		&& attemptedPayloadSignature !== ''
		&& currentPayloadSignature !== ''
		&& attemptedPayloadSignature === currentPayloadSignature;
	if (sameFailedSignature) {
		if (hasRenderedMarkup) {
			return {
				action: 'skip',
				reason: 'reuse_rendered_error',
			};
		}

		if (hasCachedMarkup) {
			return {
				action: 'rehydrate',
				reason: 'reuse_cached_error_markup',
			};
		}

		return {
			action: 'fetch',
			reason: 'error_without_rendered_markup',
		};
	}

	return {
		action: 'fetch',
		reason: 'fetch_needed',
	};
}

/**
* Responsibility:
* - Ensure ROI result is available for currently active result page.
*
* Touches:
* - Reads page state from GF lifecycle data and executes decision path.
*
* Guarantees:
* - Fetches only when active page is ROI result page and decision requires it.
*/
function ensureRoiResultForCurrentPage(app, form, { force = false, reason = '', currentPage = 0 } = {}) {
	const resultPage = getResultPage(form, app.config);
	if (!resultPage) {
		return;
	}

	const resultPageNumber = getPageNumber(resultPage);
	const currentPageNumber = Number.isFinite(Number(currentPage)) && Number(currentPage) > 0
		? Math.trunc(Number(currentPage))
		: readCurrentPageNumber(app);
	if (currentPageNumber <= 0 || currentPageNumber !== resultPageNumber) {
		return;
	}

	if (isRoiLoading(form)) {
		return;
	}

	const payloadResult = buildPayload(form, app.config.roiInputMap);
	const payload = payloadResult.payload;
	const currentPayloadSignature = buildPayloadSignature(payload);
	const successfulPayloadSignature = readRoiPayloadSignature(form);
	const attemptedPayloadSignature = readRoiAttemptSignature(form);
	const status = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status');
	const roiReady = isRoiReady(form, app.config);
	const container = getResultContainerFromPage(resultPage, app.config);
	const cached = readResultMarkupCache(roiMarkupCacheByForm, app.config.formId, currentPayloadSignature);
	const decision = getRoiFetchDecision({
		force,
		roiReady,
		status,
		errorStatus: app.config.statuses.error,
		currentPayloadSignature,
		successfulPayloadSignature,
		attemptedPayloadSignature,
		hasRenderedMarkup: hasRenderedResult(container),
		hasCachedMarkup: cached !== null,
	});

	if (decision.action === 'skip') {
		if (cached) {
			setRoiNoResults(form, cached.noResults);
			syncResultState(form, app.config);
		} else if (syncNoResultsFromContainer(form, container)) {
			syncResultState(form, app.config);
		}
		maybeFinalizeSuccessfulResult({
			app,
			form,
			page: resultPage,
			payload,
			payloadSignature: currentPayloadSignature,
		});
		return;
	}

	if (decision.action === 'rehydrate') {
		if (rehydrateRoiFromCache(app, form, container, currentPayloadSignature)) {
			maybeFinalizeSuccessfulResult({
				app,
				form,
				page: resultPage,
				payload,
				payloadSignature: currentPayloadSignature,
			});
			return;
		}
	}

	app.logger.debug('Starting ROI result on dedicated result step.', {
		formId: app.config.formId,
		reason,
		decision: decision.reason,
		status,
		currentPage: currentPageNumber,
		resultPage: getPageNumber(resultPage),
		currentPayloadSignature,
		successfulPayloadSignature,
		attemptedPayloadSignature,
	});

	resultAtRoiStep({
		app,
		form,
		page: resultPage,
		autoRetry: true,
		payloadOverride: payload,
		payloadFieldErrorsOverride: payloadResult.fieldErrors,
		payloadSignatureOverride: currentPayloadSignature,
	});
}

/**
* Responsibility:
* - Invalidate ROI result state when tracked ROI inputs change.
*
* Touches:
* - Aborts active requests, clears signatures/cache/no-results, marks stale status.
*
* Guarantees:
* - Keeps stale state deterministic after relevant input changes.
*/
function invalidateRoiIfNeeded(app, form, eventTarget, trackedNames) {
	if (!(eventTarget instanceof Element)) {
		return;
	}

	const inputName = eventTarget.getAttribute('name') || '';
	if (!trackedNames.has(inputName)) {
		return;
	}

	const currentPayloadSignature = buildPayloadSignature(buildPayload(form, app.config.roiInputMap).payload);
	const successfulPayloadSignature = readRoiPayloadSignature(form);
	const currentCtx = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_advice_ctx');
	const loading = isRoiLoading(form);
	const roiReady = isRoiReady(form, app.config);
	if (
		!loading
		&& roiReady
		&& successfulPayloadSignature !== ''
		&& currentPayloadSignature !== ''
		&& successfulPayloadSignature === currentPayloadSignature
	) {
		return;
	}

	if (!currentCtx && !loading) {
		return;
	}

	abortActiveRoiRequest(app.config.formId);

	if (loading) {
		invalidateRequest(form, ROI_REQUEST_SEQ_DATA_KEY, ROI_REQUEST_ACTIVE_DATA_KEY);
		setRoiLoading(form, false);
	}

	clearRoiNoResults(form);
	clearResultMarkupCache(roiMarkupCacheByForm, app.config.formId);
	writeRoiPayloadSignature(form, '');
	writeRoiAttemptSignature(form, '');
	writeFinalizeState(form, 'idle', '', '', 0);
	writeHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status', app.config.statuses.stale);
	syncResultState(form, app.config);

	const container = getResultContainer(form, app.config);
	if (container) {
		renderNotReady(container, app.config);
	}
}

/**
* Responsibility:
* - Synchronize ROI UI from persisted form state after (re)render.
*
* Touches:
* - Restores signature/no-results state and triggers page-aware fetch decision.
*
* Guarantees:
* - UI and hidden state remain aligned after GF rerenders.
*/
function syncRoiUiState(app, form) {
	if (isRoiLoading(form)) {
		const container = getResultContainer(form, app.config);
		if (container) {
			renderLoading(container, app.config);
		}
		syncResultState(form, app.config);
		return;
	}

	const status = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status');

	if (isRoiReady(form, app.config)) {
		if (readRoiPayloadSignature(form) === '') {
			const payload = buildPayload(form, app.config.roiInputMap).payload;
			writeRoiPayloadSignature(form, buildPayloadSignature(payload));
		}

		const cached = readResultMarkupCache(roiMarkupCacheByForm, app.config.formId, readRoiPayloadSignature(form));
		let restoredFromCacheOrDom = false;
		if (cached) {
			setRoiNoResults(form, cached.noResults);
			restoredFromCacheOrDom = true;
		} else {
			const container = getResultContainer(form, app.config);
			restoredFromCacheOrDom = syncNoResultsFromContainer(form, container);
		}
		if (!restoredFromCacheOrDom) {
			clearRoiNoResults(form);
		}
	} else {
		clearRoiNoResults(form);
		if (status === app.config.statuses.stale) {
			const container = getResultContainer(form, app.config);
			if (container) {
				renderNotReady(container, app.config);
			}
		}
	}

	syncResultState(form, app.config);
	ensureRoiResultForCurrentPage(app, form, {
		force: false,
		reason: 'sync',
	});
}

/**
* Responsibility:
* - Register native GF pre-submission gate for ROI result transition control.
*
* Touches:
* - Registers one async filter per form on `gform/submission/pre_submission`.
*
* Guarantees:
 * - Uses `data.abort = true` while ROI request is loading.
*/
function registerRoiPreSubmissionFilter(app, resultPageNumber) {
	const formId = Number(app.config.formId || 0);
	if (!Number.isFinite(formId) || formId <= 0 || resultPageNumber <= 0) {
		return;
	}

	const registrationKey = String(formId);
	if (registeredPreSubmissionFilterForms.has(registrationKey)) {
		return;
	}

	const addAsyncFilter = window?.gform?.utils?.addAsyncFilter;
	if (typeof addAsyncFilter !== 'function') {
		app.logger.error('GF pre-submission hook unavailable: cannot register native ROI transition gate.', {
			formId,
			whatWentWrong: 'gform.utils.addAsyncFilter is not available in this runtime.',
			likelyCause: 'GF frontend script utilities did not initialize before configurator binding.',
			howToFix: 'Ensure GF scripts are loaded and bind configurator after `gform/theme/scripts_loaded`.',
		});
		return;
	}

	addAsyncFilter('gform/submission/pre_submission', async (data) => {
		const targetForm = data?.form;
		if (!(targetForm instanceof HTMLElement)) {
			return data;
		}

		const submittedFormId = getFormIdFromElement(targetForm);
		if (submittedFormId !== formId) {
			return data;
		}

		if (isPreviousSubmissionType(data)) {
			abortActiveRoiRequest(formId);
			if (isRoiLoading(targetForm)) {
				invalidateRequest(targetForm, ROI_REQUEST_SEQ_DATA_KEY, ROI_REQUEST_ACTIVE_DATA_KEY);
				setRoiLoading(targetForm, false);
			}
			syncResultState(targetForm, app.config);
			return data;
		}

		const isNext = isNextSubmissionType(data);
		const isSubmit = isSubmitSubmissionType(data);
		if (!isNext && !isSubmit) {
			return data;
		}

		const sourcePage = readSubmissionSourcePage(targetForm, formId) || readCurrentPageNumber(app);
		if (sourcePage <= 0 || sourcePage !== resultPageNumber) {
			return data;
		}

		if (shouldDisableResultAction(targetForm, app.config)) {
			data.abort = true;
			syncResultState(targetForm, app.config);
			return data;
		}

		return data;
	});

	registeredPreSubmissionFilterForms.add(registrationKey);
}

/**
* Responsibility:
* - Register ROI boundary/result runtime bindings for configured GF form.
*
* Touches:
* - Attaches click/input/change listeners and native pre-submission gate.
*
* Guarantees:
* - Binds once per form instance and keeps ROI flow state synchronized.
*/
export function bindBoundaryRoiFetch(app) {
	const form = getForm(app.config);
	if (!form) {
		return;
	}

	const missingHiddenFields = validateHiddenFieldMap(app.config);
	if (missingHiddenFields.length > 0) {
		app.logger.error('ROI state mapping is incomplete: required hidden fields are missing.', {
			formId: app.config.formId,
			missingHiddenFields,
			whatWentWrong: 'One or more required hidden state fields could not be mapped.',
			likelyCause: 'Hidden fields are missing from the form or not mapped via expected input names.',
			howToFix: 'Add/verify hidden fields: sd_advice_ctx, sd_advice_hash, sd_roi_status.',
		});
		return;
	}

	if (!validateLayout(app, form)) {
		return;
	}

	syncRoiUiState(app, form);

	if (form.dataset.sdConfigRoiBound === '1') {
		return;
	}

	const trackedNames = buildTrackedNames(app.config.roiInputMap);
	const boundaryPageNumber = getPageNumber(getBoundaryPage(form, app.config));
	const resultPageNumber = getPageNumber(getResultPage(form, app.config));
	registerRoiPreSubmissionFilter(app, resultPageNumber);

	const mappedRoiKeys = Object.keys(app.config.roiInputMap || {});
	const expectedRoiKeys = Array.isArray(app.config.expectedRoiKeys)
		? app.config.expectedRoiKeys
		: [];
	const missingRoiKeys = expectedRoiKeys.filter((key) => !mappedRoiKeys.includes(key));
	if (missingRoiKeys.length > 0) {
		app.logger.error('ROI input mapping is incomplete: some API payload keys have no source field.', {
			formId: app.config.formId,
			mappedRoiKeys,
			missingRoiKeys,
			whatWentWrong: `Expected ${expectedRoiKeys.length} mapped keys, found ${mappedRoiKeys.length}.`,
			likelyCause: 'One or more fields are missing dynamic populate parameter names or mapping.',
			howToFix: 'Add/verify mapping for each missing key listed in `missingRoiKeys`, then reload the form.',
		});
	}

	form.addEventListener(
		'click',
		(event) => {
			const target = event.target;
			if (!(target instanceof Element)) {
				return;
			}

			const previousButton = target.closest('.gform_previous_button');
			if (previousButton instanceof HTMLButtonElement || previousButton instanceof HTMLInputElement) {
				abortActiveRoiRequest(app.config.formId);
				if (isRoiLoading(form)) {
					invalidateRequest(form, ROI_REQUEST_SEQ_DATA_KEY, ROI_REQUEST_ACTIVE_DATA_KEY);
					setRoiLoading(form, false);
				}
				syncResultState(form, app.config);
				return;
			}

			const nextButton = target.closest('.gform_next_button');
			if (nextButton instanceof HTMLButtonElement || nextButton instanceof HTMLInputElement) {
				const currentPageNumber = getPageNumber(nextButton.closest('.gform_page'));
				if (boundaryPageNumber > 0 && currentPageNumber === boundaryPageNumber) {
					app.logger.debug('ROI boundary next clicked; waiting for GF successful page transition before ROI result.', {
						formId: app.config.formId,
						currentPage: currentPageNumber,
					});
					return;
				}

				if (resultPageNumber <= 0 || currentPageNumber !== resultPageNumber) {
					return;
				}

				if (!isRoiNoResults(form)) {
					const container = getResultContainer(form, app.config);
					syncNoResultsFromContainer(form, container);
				}

				syncResultState(form, app.config);
				return;
			}

			const submitButton = target.closest('button[type="submit"], input[type="submit"]');
			if (!(submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement)) {
				return;
			}

			const currentPageNumber = getPageNumber(submitButton.closest('.gform_page'));
			if (resultPageNumber <= 0 || currentPageNumber !== resultPageNumber) {
				return;
			}

			if (!isRoiNoResults(form)) {
				const container = getResultContainer(form, app.config);
				syncNoResultsFromContainer(form, container);
			}

			syncResultState(form, app.config);
		},
		true
	);

	const handleInvalidate = (event) => {
		invalidateRoiIfNeeded(app, form, event.target, trackedNames);
	};
	form.addEventListener('input', handleInvalidate, true);
	form.addEventListener('change', handleInvalidate, true);
	form.dataset.sdConfigRoiBound = '1';

	app.logger.debug('Boundary ROI fetch handlers attached.', {
		formId: app.config.formId,
		boundaryClass: app.config.roiBoundaryClass,
		trackedInputCount: trackedNames.size,
	});
}
